import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import cx from 'helpers/cx'

import type { ButtonSize, ButtonVariant } from './Button'

import classes from './MenuButton.module.scss'

const SIZE_CLASS: Record<ButtonSize, string> = {
  xs: 'btn-xs',
  sm: 'btn-sm',
  md: 'btn-md',
  lg: 'btn-lg',
  input: 'btn-input',
}

const VARIANT_CLASS: Record<Exclude<ButtonVariant, 'textLink'>, string> = {
  primary: 'btn-primary',
  accent: 'btn-accent',
  cancel: 'btn-cancel',
  nature: 'btn-nature',
  danger: 'btn-danger',
  ghostAccent: 'btn-ghost-accent',
}

export type MenuButtonItem = {
  id: string
  label: string
  disabled?: boolean
}

export type MenuButtonProps = {
  items: MenuButtonItem[]
  defaultSelectedId: string
  onAction: (id: string) => void
  variant?: Extract<ButtonVariant, 'primary' | 'accent'>
  size?: ButtonSize
  loading?: boolean
  loadingText?: string
  disabled?: boolean
  className?: string
  menuAriaLabel?: string
}

const MenuButton = ({
  items,
  defaultSelectedId,
  onAction,
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingText,
  disabled = false,
  className,
  menuAriaLabel,
}: MenuButtonProps) => {
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(defaultSelectedId)
  const rootRef = useRef<HTMLDivElement>(null)
  const isDisabled = disabled || loading
  const paintClass = VARIANT_CLASS[variant]
  const sizeClass = SIZE_CLASS[size]
  const selectedItem = items.find((item) => item.id === selectedId) || items[0]
  const actionLabel =
    loading && loadingText ? loadingText : selectedItem?.label || defaultSelectedId

  useEffect(() => {
    if (loading) {
      setOpen(false)
    }
  }, [loading])

  useEffect(() => {
    if (!open) return undefined

    const onOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('click', onOutsideClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('click', onOutsideClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const handleMenuSelect = (id: string) => {
    setOpen(false)
    if (isDisabled) return
    setSelectedId(id)
  }

  const handleAction = () => {
    if (isDisabled || !selectedItem || selectedItem.disabled) return
    onAction(selectedItem.id)
  }

  return (
    <div className={cx(classes.root, className)} ref={rootRef}>
      <div
        className={cx(
          'btn',
          paintClass,
          sizeClass,
          classes.trigger,
          loading && classes.triggerLoading
        )}
        aria-disabled={isDisabled ? 'true' : undefined}
        aria-busy={loading ? 'true' : undefined}
      >
        <button
          type="button"
          className={classes.action}
          disabled={isDisabled || selectedItem?.disabled}
          onClick={handleAction}
        >
          {actionLabel}
        </button>
        <button
          type="button"
          className={classes.menuToggle}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${selectedItem?.label || 'Action'} options`}
          disabled={isDisabled}
          onClick={(event) => {
            event.stopPropagation()
            setOpen((prev) => !prev)
          }}
        >
          <ChevronDown
            aria-hidden
            size={14}
            className={cx(classes.icon, open && classes.iconOpen)}
          />
        </button>
      </div>

      {open && (
        <div
          className={classes.menu}
          role="menu"
          aria-label={menuAriaLabel || `${selectedItem?.label || 'Action'} options`}
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              aria-current={item.id === selectedId ? 'true' : undefined}
              className={cx(
                classes.menuItem,
                item.id === selectedId && classes.menuItemSelected
              )}
              disabled={item.disabled || isDisabled}
              onClick={() => handleMenuSelect(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export { MenuButton }
